import { trackEvent } from '@services/tracking';

type PostType = 'Creator' | 'Consumer';

type PostForAnalytics = {
  author: { id: number };
  media: Array<{ video?: string | null }>;
  reactions?: Array<{ type: string; count?: number }>;
};

type ReplyForAnalytics = {
  author: { id: number };
};

const derivePostAnalytics = (
  post: PostForAnalytics,
  influencerId: number,
): { postType: PostType; mediaType: string } => ({
  postType: post.author.id === influencerId ? 'Creator' : 'Consumer',
  mediaType: post.media.map((m) => (m.video ? 'video' : 'image')).join(', '),
});

export const communityAnalytics = {
  trackPostViewed: (post: PostForAnalytics, influencerId: number) => {
    const { postType, mediaType } = derivePostAnalytics(post, influencerId);
    trackEvent({
      type: 'Post Viewed',
      payload: {
        'Has Comments': String(
          post.reactions?.some((r) => r.type === 'Comment') ?? false,
        ),
        'Has Reactions': String((post.reactions?.length ?? 0) > 0),
        'Media Count': post.media.length,
        'Number of comments':
          post.reactions?.filter((r) => r.type === 'Comment').length ?? 0,
        'Number of reactions': post.reactions?.length ?? 0,
        'Post Type': postType,
        'Media Type': mediaType,
      },
    });
  },

  trackPostDeleted: (post: PostForAnalytics, influencerId: number) => {
    const { postType, mediaType } = derivePostAnalytics(post, influencerId);
    trackEvent({
      type: 'Post Deleted',
      payload: {
        'Media Type': mediaType,
        'Post Owner': postType,
      },
    });
  },

  trackReactionAddedOnPost: (post: PostForAnalytics, influencerId: number) => {
    const { postType, mediaType } = derivePostAnalytics(post, influencerId);
    trackEvent({
      type: 'Reaction Added',
      payload: {
        'Media Type': mediaType,
        'Post Type': postType,
        'Reaction Location': 'Post',
      },
    });
  },

  trackCommentsExpanded: (trigger: 'Comment Count' | 'Add Comment') => {
    trackEvent({
      type: 'Comments Expanded',
      payload: { Trigger: trigger },
    });
  },

  trackCommentAdded: (post: PostForAnalytics, influencerId: number) => {
    const { postType, mediaType } = derivePostAnalytics(post, influencerId);
    trackEvent({
      type: 'Comment Added',
      payload: {
        'Post Type': postType,
        'Media Type': mediaType,
      },
    });
  },

  trackCommentDeleted: (reply: ReplyForAnalytics, influencerId: number) => {
    trackEvent({
      type: 'Comment Deleted',
      payload: {
        'Comment Owner':
          reply.author.id === influencerId ? 'Creator' : 'Consumer',
        'Media Type': 'text',
      },
    });
  },

  trackViewPreviousComments: () => {
    trackEvent({
      type: 'View Previous Comments Tapped',
      payload: {},
    });
  },

  trackCommunityViewed: (
    firstPagePosts: Array<{ author: { id: number } }>,
    influencerId: number,
  ) => {
    trackEvent({
      type: 'Community Viewed',
      payload: {
        'Includes a consumer post': String(
          firstPagePosts.some((post) => post.author.id !== influencerId),
        ),
        'Includes a creator post': String(
          firstPagePosts.some((post) => post.author.id === influencerId),
        ),
      },
    });
  },
};
